import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environment";
import { Observable, catchError, firstValueFrom, map } from "rxjs";
import { DropdownItem, LateEntry } from "../interfaces/dropdown-item.interface";
import {
  CptCodesLookup,
  MedicalDiagnosisLookup,
  TreatmentCodesLookup,
} from "../interfaces/patient.interface";
import { Lookup, Parameter } from "../../features/epoc/epoc.interface";
import { Mod } from "../interfaces/mod.interface";
import { EpocService } from "../../features/epoc/epoc.service";
import { DisplayValue } from "src/app/common/components/labour-hours/labour-hours.component";
import { TherapistDropdownItem, TherapistDropdownResponse } from "../interfaces/home/tasks.interface";
import { ApiResponse } from "../interfaces/certification.interface";
import { certificationErrors } from "../constants/certification-constants";

@Injectable({ providedIn: "root" })
export class LookupClient {
  endpoint = "lookup";
  constructor(
    private httpClient: HttpClient,
    private epocService: EpocService
  ) {}

  getCountiesLookup(state: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/counties/${state}`
    );
  }

  getWageIndexByCounty(county: string): Observable<string | null> {
    return this.httpClient.get<string | null>(
      `${environment.endpoint}/lookup/wage-index/${county}`
    );
  }

  getFacilityTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/facilitytypes`
    );
  }

  getLateEntryReasons(): Observable<LateEntry[]> {
    return this.httpClient.get<LateEntry[]>(
      `${environment.endpoint}/lookup/late-entry-reasons`
    );
  }

  getInterfaceTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/interface-types`
    );
  }

  getContactTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/contact-types`
    );
  }

  getReferralTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/referral-types`
    );
  }

  getMACFIs(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/macfies`
    );
  }

  getLCDProviders(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/lcd-providers`
    );
  }

  getTimezones(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/timezones`
    );
  }

  getStates(useStateCode: boolean = false): Observable<DropdownItem[]> {
    const params = new HttpParams().set("useStateCode", String(useStateCode));

    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/states`,
      { params }
    );
  }

  getPayTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/pay-types`
    );
  }

  getPhysicianTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/physician-types`
    );
  }

  getPhysicianSpecialties(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/physician-specialties`
    );
  }

  getTaxonomy(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/taxonomies`
    );
  }

  // 1: Manual Entry, 2: EMR
  getDataSources(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/data-sources`
    );
  }

  getAdmitFrom(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/admission-froms`
    );
  }

  getDischargeTo(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/discharge-tos`
    );
  }

  getAdmissionTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/admission-types`
    );
  }

  getAdmissionTypesByFacility(facilityId: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/admission-types/${facilityId}`
    );
  }

  getDocTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/doc-types`
    );
  }

  getDisciplines(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/disciplines`
    );
  }

  getUserDisciplines(): Observable<any[]> {
    return this.httpClient
      .get<any[]>(
        `${environment.endpoint}/patientdocumentation/get-User-Discipline`
      )
      .pipe(
        map((response: any) => {
          return response?.responseData;
        })
      );
  }

  getDiagnosisCodes(
    search: string,
    facilityId: string,
    disciplineId: string
  ): Observable<MedicalDiagnosisLookup[]> {
    return this.httpClient.get<MedicalDiagnosisLookup[]>(
      `${environment.endpoint}/lookup/diagnosis-codes?keyword=${search}&facilityid=${facilityId}&disciplineid=${disciplineId}`
    );
  }

  getTreatmentCodes(
    search: string,
    facilityId: string,
    disciplineId: string
  ): Observable<TreatmentCodesLookup[]> {
    return this.httpClient.get<TreatmentCodesLookup[]>(
      `${environment.endpoint}/lookup/treatment-codes?keyword=${search}&facilityid=${facilityId}&disciplineid=${disciplineId}`
    );
  }

  getCptCodes(search: string): Observable<CptCodesLookup[]> {
    return this.httpClient.get<CptCodesLookup[]>(
      `${environment.endpoint}/lookup/cpt-codes?keyword=${search}`
    );
  }

  getDurationTypes(
    visittypeid?: number,
    frequencytypeid?: number
  ): Observable<DropdownItem[]> {
    let url = `${environment.endpoint}/lookup/duration-types`;

    if (visittypeid && !frequencytypeid) {
      url += `?visittypeid=${visittypeid}`;
    }
    if (frequencytypeid && !visittypeid) {
      url += `?frequencytypeid=${frequencytypeid}`;
    }

    if (visittypeid && frequencytypeid) {
      url += `?visittypeid=${visittypeid}&frequencytypeid=${frequencytypeid}`;
    }

    return this.httpClient.get<DropdownItem[]>(url);
  }

  getFrequencies(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/frequencies`
    );
  }

  getFrequencyTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/frequency-types`
    );
  }

  getDurationValues(): Observable<DropdownItem[]> {
    return this.httpClient.get<{ responseData: DropdownItem[] }>(
      `${environment.endpoint}/lookup/get-duration-values`
    ).pipe(map((response) => response.responseData));
  }

  getVisitTypes(frequencyid?: number): Observable<DropdownItem[]> {
    let url = `${environment.endpoint}/lookup/visit-types`;

    if (frequencyid) {
      url += `?frequencyid=${frequencyid}`;
    }

    return this.httpClient.get<DropdownItem[]>(url);
  }

  getEOCReasons(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/eoc-reasons`
    );
  }

  getEOCDestinations(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/eoc-destinations`
    );
  }

  getMod(): Observable<Mod[]> {
    return this.httpClient.get<Mod[]>(
      `${environment.endpoint}/lookup/billing-mod`
    );
  }

  getMethodologies(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/methodology`
    );
  }

  getValuesByLookupId(
    lookup: Lookup,
    facilityId: string,
    isCustomized: boolean,
    patientTherapyId: number = 0
  ): Observable<DropdownItem[]> {
    if (lookup?.Id) {
      let params: any = {
        ...lookup,
        patientTherapyId: patientTherapyId
      };

      Object.keys(params).forEach(
        (key) =>
          (params[key] === undefined || params[key] === null) &&
          delete params[key]
      );

      return this.httpClient
        .post<{ responseData: DropdownItem[] }>(
          `${environment.endpoint}/lookup/get-values-by-lookupid`,
          {
            ...params,
          }
        )
        .pipe(map((response) => response.responseData));
    }

    let params: any = {
      facilityId: facilityId,
      PatientTherapyId : patientTherapyId
    };

    let parameters: Parameter[] = [];
    if (lookup?.EndpointInfo.Type.toLowerCase() === "get") {
      parameters = lookup.EndpointInfo?.Parameters;
    } else {
      if (lookup?.EndpointInfo?.Parameters?.[0].Object) {
        parameters = lookup?.EndpointInfo?.Parameters?.[0].Object;
      } else {
        parameters = lookup?.EndpointInfo?.Parameters;
      }
    }

    parameters?.forEach((param) => {
      if (param.Value !== null && param.Value !== undefined) {
        if (typeof param.Value === "string" && param.Value.startsWith("{")) {
          const fieldId = param.Value.substring(1, param.Value.length - 1);
          let fieldIdToPass = this.epocService.document?.NavigationSteps.map(
            (step) => step.Sections
          )
            .flat()
            .map((section) => section?.Fields)
            .flat()
            .find((field) => field?.FieldId === fieldId)?.Value;

          if (!fieldIdToPass) {
            fieldIdToPass = this.epocService.document?.NavigationSteps.map(
              (step) => step.Sections
            )
              .flat()
              .map((section) => section?.Questionnaire)
              .flat()
              .map((questionnaire) => questionnaire?.Questions)
              .flat()
              .find((field) => field?.QuestionId === fieldId)?.Value;
          }

          const valueKey = Object.keys(fieldIdToPass || {})?.[0];

          if (valueKey) {
            const value = fieldIdToPass?.[valueKey];

            if (!value) {
              return;
            }
            if (typeof value === "string" && value.startsWith("{")) {
              return;
            } else {
              params[param.ParameterName] = value;
            }
          }
        } else {
          params[param.ParameterName] = param.Value;
        }
      }
    });

    if (lookup?.EndpointInfo.Type.toLowerCase() === "get") {
      let url = `${environment.endpoint}/${lookup.EndpointInfo.Url}`;

      return this.httpClient
        .get<{ data?: DropdownItem[]; responseData: DropdownItem[] }>(url, {
          params: params,
        })
        .pipe(
          map((response) => {
            if (response.data) {
              return response.data;
            }
            return response.responseData;
          })
        );
    }

    return this.httpClient
      .post<{ responseData: DropdownItem[] }>(
        `${environment.endpoint}/${lookup?.EndpointInfo.Url}`,
        params
      )
      .pipe(map((response) => response.responseData));
  }

  getValuesForImpairment(
    lookup: Lookup,
    impairment: any,
    patientTherapyId: number = 0,
    isPlofLookup: boolean = false
  ): Observable<DropdownItem[]> {
    let params: any = {};
    let parameters: Parameter[] = [];
    if (lookup.EndpointInfo.Type.toLowerCase() === "get") {
      parameters = lookup.EndpointInfo?.Parameters;
    } else {
      if (lookup.EndpointInfo?.Parameters?.[0].Object) {
        parameters = lookup.EndpointInfo?.Parameters?.[0].Object;
      } else {
        parameters = lookup.EndpointInfo?.Parameters;
      }
    }

    parameters?.forEach((param) => {
      if (param.Value !== null && param.Value !== undefined) {
        if (typeof param.Value === "string" && param.Value.startsWith("{")) {
          const fieldId = param.Value.substring(1, param.Value.length - 1);
          let value;
          if (fieldId.includes("Area")) {
            value = impairment.AreaId;
          } else if (fieldId.includes("Subcategory")) {
            value = impairment.SubcategoryId;
          }

          params[param.ParameterName] = value;
        } else {
          params[param.ParameterName] = param.Value;
        }
      }
    });

    params['PatientTherapyId'] = patientTherapyId;
    params['IsPlofLookup'] = isPlofLookup;
    
    let url = `${environment.endpoint}/${lookup.EndpointInfo.Url}`;

    return this.httpClient
      .get<{ data?: DropdownItem[]; responseData: DropdownItem[] }>(url, {
        params: params,
      })
      .pipe(
        map((response) => {
          if (response.data) {
            return response.data;
          }
          return response.responseData;
        })
      );
  }

  async getStatusOfSection(lookup: Lookup,patientTherapyId: number = 0) {
    let params: any = {};

    let parameters: Parameter[] = [];
    if (lookup?.EndpointInfo.Type.toLowerCase() === "get") {
      parameters = lookup.EndpointInfo?.Parameters;
    } else {
      if (lookup?.EndpointInfo?.Parameters?.[0].Object) {
        parameters = lookup.EndpointInfo?.Parameters?.[0].Object;
      } else {
        parameters = lookup?.EndpointInfo?.Parameters;
      }
    }

    parameters?.forEach((param) => {
      if (param.Value !== null && param.Value !== undefined) {
        if (typeof param.Value === "string" && param.Value.startsWith("{")) {
          const fieldId = param.Value.substring(1, param.Value.length - 1);
          let fieldIdToPass = this.epocService.document?.NavigationSteps.map(
            (step) => step.Sections
          )
            .flat()
            .map((section) => section?.Fields)
            .flat()
            .find((field) => field?.FieldId === fieldId)?.Value;

          if (!fieldIdToPass) {
            fieldIdToPass = this.epocService.document?.NavigationSteps.map(
              (step) => step.Sections
            )
              .flat()
              .map((section) => section?.Questionnaire)
              .flat()
              .map((questionnaire) => questionnaire?.Questions)
              .flat()
              .find((field) => field?.QuestionId === fieldId)?.Value;
          }

          const valueKey = Object.keys(fieldIdToPass || {})?.[0];

          if (valueKey) {
            const value = fieldIdToPass?.[valueKey];

            if (!value) {
              return;
            }
            params[param.ParameterName] = value;
          }
        } else {
          params[param.ParameterName] = param.Value;
        }
      }
    });

    params['PatientTherapyId'] = patientTherapyId;

    return await firstValueFrom(
      this.httpClient.get<{ responseData: any }>(
        `${environment.endpoint}/${lookup?.EndpointInfo.Url}`,
        {
          params: params,
        }
      )
    );
  }

  async getGridQuestionData(params: any, url: string) {
    return await firstValueFrom(
      this.httpClient
        .get<{ responseData: any }>(`${environment.endpoint}/${url}`, {
          params: params,
        })
        .pipe(map((response) => response.responseData))
    );
  }

  getFomsGoals(lookup: Lookup, facilityId : number, patientTherapyId: number = 0): Observable<DropdownItem[]> {
    let params: any = {};

    let parameters: Parameter[] = [];
    if (lookup.EndpointInfo?.Type.toLowerCase() === "get") {
      parameters = lookup.EndpointInfo?.Parameters;
    } else {
      if (lookup.EndpointInfo?.Parameters?.[0].Object) {
        parameters = lookup.EndpointInfo?.Parameters?.[0].Object;
      } else {
        parameters = lookup.EndpointInfo?.Parameters;
      }
    }

    parameters?.forEach((param) => {
      if (param.Value !== null && param.Value !== undefined) {
        if (typeof param.Value === "string" && param.Value.startsWith("{")) {
          const fieldId = param.Value.substring(1, param.Value.length - 1);
          let fieldIdToPass = this.epocService.document?.NavigationSteps.map(
            (step) => step.Sections
          )
            .flat()
            .map((section) => section?.Fields)
            .flat()
            .find((field) => field?.FieldId === fieldId)?.Value;

          if (!fieldIdToPass) {
            fieldIdToPass = this.epocService.document?.NavigationSteps.map(
              (step) => step.Sections
            )
              .flat()
              .map((section) => section?.Questionnaire)
              .flat()
              .map((questionnaire) => questionnaire?.Questions)
              .flat()
              .find((field) => field?.QuestionId === fieldId)?.Value;
          }

          const valueKey = Object.keys(fieldIdToPass || {})?.[0];

          if (valueKey) {
            const value = fieldIdToPass?.[valueKey];

            if (!value) {
              return;
            }
            params[param.ParameterName] = value;
          }
        } else {
          params[param.ParameterName] = param.Value;
        }
      }
    });

    params['facilityId'] = facilityId;
    params['PatientTherapyId'] = patientTherapyId;

    if (lookup.EndpointInfo?.Type.toLowerCase() === "get") {
      let url = `${environment.endpoint}/${lookup?.EndpointInfo?.Url}`;

      return this.httpClient
        .get<{ data?: DropdownItem[]; responseData: DropdownItem[] }>(url, {
          params: params,
        })
        .pipe(
          map((response) => {
            if (response.data) {
              return response.data;
            }
            return response.responseData;
          })
        );
    }

    return this.httpClient
      .post<{ responseData: DropdownItem[] }>(
        `${environment.endpoint}/${lookup.EndpointInfo.Url}`,
        params
      )
      .pipe(map((response) => response.responseData));
  }

  getWageIndeces() {
    return this.httpClient
      .get<{ responseData: any }>(`${environment.endpoint}/lookup/wageindex`)
      .pipe(map((response) => response.responseData));
  }
  
  getLabourHoursTimePeriodOptions() {
    return this.httpClient.get<Array<DisplayValue<number>>>(
      `${environment.endpoint}/${this.endpoint}/labourhours-timeperiod`
    );
  }

  getOverTimePeriodOptions() {
    return this.httpClient.get<Array<DisplayValue<number>>>(
      `${environment.endpoint}/${this.endpoint}/overtimeperiod`
    );
  }

  getWeekTimeLineOptions() {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/weektimeline`
    );
  }

  getDCGGSectionAssessmentDate(lookup : Lookup){
    const endpointConfig = lookup?.EndpointInfo;
    const params:any ={}

    endpointConfig?.Parameters?.forEach((param) => {
      params[param.ParameterName] = param.Value;
    })

    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${lookup?.EndpointInfo.Url}`,
        {
          params: params,
        }
      )
      .pipe(map((response) => response.responseData));
    
  }
  getAllRateDisciplines() {
    return this.httpClient.get<Array<DisplayValue<number>>>(
      `${environment.endpoint}/${this.endpoint}/get-all-rate-disciplines`
    );
  }

  getDocumentDeletionReasons(documentTypeId: number){
    return this.httpClient.get<{responseData : DropdownItem[]}>(
      `${environment.endpoint}/${this.endpoint}/get-document-deletion-reasons`,
      {
        params: {
          documentTypeId: documentTypeId,
        },
      }
    )?.pipe(map((response) => response.responseData));
  }

  getVendorIntegrations(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/vendor-integrations`
    );
  }
  getLicensureRateDisciplinePermission(facilityId: string, userid: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${environment.endpoint}/lookup/add-licensure-discipline-permission/${facilityId}/${userid}`
    );
  }

  getAllFacilityPatient(facilityId: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/get-all-facility-patients/${facilityId}`
    );
  }

  getAllFacilityTherapistWithDiscipline(
    facilityId: string,
    isOnlyTherapist: boolean
  ) {
    return this.httpClient.get<TherapistDropdownResponse[]>(
      `${environment.endpoint}/lookup/get-all-facility-therapists/${facilityId}/${isOnlyTherapist}`
    );
  }

  getAllFacilityPhysician(facilityId: string): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/lookup/get-all-facility-physicians/${facilityId}`
    );
  }

  getCloseoutTypes(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-close-out-types`
    );
  }

  getCloseoutCycles(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-close-out-cycles`
    );
  }

  getCloseoutCompletionDates(): Observable<DropdownItem[]> {
    return this.httpClient.get<DropdownItem[]>(
      `${environment.endpoint}/${this.endpoint}/get-close-out-completion-dates`
    );
  }
}
